import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, getAuthToken } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/constants';
import {
  ChatBubbleLeftIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  EyeIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

interface Comment {
  id: string;
  author_name: string;
  author_email: string;
  author_website?: string;
  content: string;
  status: 'pending' | 'approved' | 'denied' | 'spam';
  created_at: string;
  moderated_at?: string;
  post_title: string;
  post_slug: string;
  moderated_by_email?: string;
}

interface CommentCounts {
  pending: number;
  approved: number;
  denied: number;
  spam: number;
  total: number;
}

const AdminComments = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [comments, setComments] = useState<Comment[]>([]);
  const [counts, setCounts] = useState<CommentCounts>({
    pending: 0,
    approved: 0,
    denied: 0,
    spam: 0,
    total: 0,
  });

  const [loading, setLoading] = useState(true);
  const [selectedComments, setSelectedComments] = useState<string[]>([]);
  const [currentFilter, setCurrentFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const fetchComments = async (status: string = 'all', pageNum: number = 1) => {
    try {
      setLoading(true);
      // reset error state if you later add one

      const token = getAuthToken();
      const response = await fetch(
        `${API_ENDPOINTS.comments}/admin?status=${status}&page=${pageNum}&limit=20`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch comments');
      }

      const result = await response.json();

      if (result.success) {
        setComments(result.data.comments);
        setCounts(result.data.counts);
        setPage(result.data.pagination.page);
        setTotalPages(result.data.pagination.totalPages);
      } else {
        throw new Error(result.error || 'Failed to fetch comments');
      }
    } catch (err) {
      console.error('Fetch comments error:', err);
      // ignored: optionally handle error UI
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchComments(currentFilter, page);
    }
  }, [isAuthenticated, currentFilter, page]);

  const moderateComment = async (
    commentId: string,
    action: 'approve' | 'deny' | 'spam'
  ) => {
    try {
      setActionLoading(commentId);

      const token = getAuthToken();
      const response = await fetch(`${API_ENDPOINTS.comments}/moderate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          comment_id: commentId,
          action,
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Refresh comments
        fetchComments(currentFilter, page);
        setSelectedComments([]);
      } else {
        alert(result.error || 'Failed to moderate comment');
      }
    } catch (error) {
      console.error('Moderate comment error:', error);
      alert('Failed to moderate comment');
    } finally {
      setActionLoading(null);
    }
  };

  const bulkModerate = async (action: 'approve' | 'deny' | 'spam') => {
    if (selectedComments.length === 0) return;

    try {
      setActionLoading('bulk');

      const token = getAuthToken();
      const response = await fetch(`${API_ENDPOINTS.comments}/moderate/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          comment_ids: selectedComments,
          action,
        }),
      });

      const result = await response.json();

      if (result.success) {
        fetchComments(currentFilter, page);
        setSelectedComments([]);
      } else {
        alert(result.error || 'Failed to moderate comments');
      }
    } catch (error) {
      console.error('Bulk moderate error:', error);
      alert('Failed to moderate comments');
    } finally {
      setActionLoading(null);
    }
  };

  const deleteComment = async (commentId: string) => {
    if (
      !confirm(
        'Are you sure you want to delete this comment? This action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      setActionLoading(commentId);

      const token = getAuthToken();
      const response = await fetch(`${API_ENDPOINTS.comments}/${commentId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (result.success) {
        fetchComments(currentFilter, page);
        setSelectedComments([]);
      } else {
        alert(result.error || 'Failed to delete comment');
      }
    } catch (error) {
      console.error('Delete comment error:', error);
      alert('Failed to delete comment');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleSelectComment = (commentId: string) => {
    setSelectedComments(prev =>
      prev.includes(commentId)
        ? prev.filter(id => id !== commentId)
        : [...prev, commentId]
    );
  };

  const selectAllVisible = () => {
    setSelectedComments(comments.map(comment => comment.id));
  };

  const clearSelection = () => {
    setSelectedComments([]);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      denied: 'bg-red-100 text-red-800',
      spam: 'bg-gray-100 text-gray-800',
    };

    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${badges[status as keyof typeof badges]}`}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading && comments.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Comments</h1>
          <p className="text-gray-600">Manage and moderate user comments</p>
        </div>
        <ChatBubbleLeftIcon className="h-8 w-8 text-gray-400" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-gray-900">{counts.total}</div>
          <div className="text-sm text-gray-600">Total Comments</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-yellow-600">
            {counts.pending}
          </div>
          <div className="text-sm text-gray-600">Pending</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-green-600">
            {counts.approved}
          </div>
          <div className="text-sm text-gray-600">Approved</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-red-600">{counts.denied}</div>
          <div className="text-sm text-gray-600">Denied</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-gray-600">{counts.spam}</div>
          <div className="text-sm text-gray-600">Spam</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              value={currentFilter}
              onChange={e => {
                setCurrentFilter(e.target.value);
                setPage(1);
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">All Comments</option>
              <option value="pending">Pending ({counts.pending})</option>
              <option value="approved">Approved ({counts.approved})</option>
              <option value="denied">Denied ({counts.denied})</option>
              <option value="spam">Spam ({counts.spam})</option>
            </select>
          </div>

          {/* Bulk Actions */}
          {selectedComments.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                {selectedComments.length} selected
              </span>
              <button
                onClick={() => bulkModerate('approve')}
                disabled={actionLoading === 'bulk'}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
              >
                Approve
              </button>
              <button
                onClick={() => bulkModerate('deny')}
                disabled={actionLoading === 'bulk'}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
              >
                Deny
              </button>
              <button
                onClick={() => bulkModerate('spam')}
                disabled={actionLoading === 'bulk'}
                className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 disabled:opacity-50"
              >
                Spam
              </button>
              <button
                onClick={clearSelection}
                className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Comments List */}
      <div className="bg-white rounded-lg border border-gray-200">
        {comments.length === 0 ? (
          <div className="text-center py-12">
            <ChatBubbleLeftIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">
              {currentFilter === 'all'
                ? 'No comments yet'
                : `No ${currentFilter} comments`}
            </p>
          </div>
        ) : (
          <>
            {/* Select All */}
            <div className="p-4 border-b border-gray-200">
              <button
                onClick={
                  selectedComments.length === comments.length
                    ? clearSelection
                    : selectAllVisible
                }
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                {selectedComments.length === comments.length
                  ? 'Deselect All'
                  : 'Select All Visible'}
              </button>
            </div>

            {/* Comments */}
            <div className="divide-y divide-gray-200">
              {comments.map(comment => (
                <div key={comment.id} className="p-6">
                  <div className="flex items-start space-x-4">
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={selectedComments.includes(comment.id)}
                      onChange={() => toggleSelectComment(comment.id)}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />

                    {/* Comment Content */}
                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                            {comment.author_name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-900">
                                {comment.author_name}
                              </span>
                              {getStatusBadge(comment.status)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {comment.author_email}
                              {comment.author_website && (
                                <span>
                                  {' '}
                                  •{' '}
                                  <a
                                    href={comment.author_website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    Website
                                  </a>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(comment.created_at)}
                        </div>
                      </div>

                      {/* Comment Content */}
                      <div className="bg-gray-50 p-3 rounded-lg mb-3">
                        <p className="text-gray-800 whitespace-pre-wrap">
                          {comment.content}
                        </p>
                      </div>

                      {/* Post Info */}
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                        <span>
                          On post: <strong>{comment.post_title}</strong>
                        </span>
                        <a
                          href={`/blog/${comment.post_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline inline-flex items-center space-x-1"
                        >
                          <EyeIcon className="h-4 w-4" />
                          <span>View Post</span>
                        </a>
                      </div>

                      {/* Moderation Info */}
                      {comment.moderated_at && (
                        <div className="text-xs text-gray-500 mb-3">
                          Moderated on {formatDate(comment.moderated_at)}
                          {comment.moderated_by_email &&
                            ` by ${comment.moderated_by_email}`}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center space-x-2">
                        {comment.status === 'pending' && (
                          <>
                            <button
                              onClick={() =>
                                moderateComment(comment.id, 'approve')
                              }
                              disabled={actionLoading === comment.id}
                              className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              <CheckIcon className="h-4 w-4 mr-1" />
                              Approve
                            </button>
                            <button
                              onClick={() =>
                                moderateComment(comment.id, 'deny')
                              }
                              disabled={actionLoading === comment.id}
                              className="inline-flex items-center px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                            >
                              <XMarkIcon className="h-4 w-4 mr-1" />
                              Deny
                            </button>
                            <button
                              onClick={() =>
                                moderateComment(comment.id, 'spam')
                              }
                              disabled={actionLoading === comment.id}
                              className="inline-flex items-center px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 disabled:opacity-50"
                            >
                              <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                              Spam
                            </button>
                          </>
                        )}

                        {comment.status !== 'pending' && (
                          <button
                            onClick={() =>
                              moderateComment(comment.id, 'approve')
                            }
                            disabled={actionLoading === comment.id}
                            className="text-sm text-green-600 hover:text-green-700"
                          >
                            Re-approve
                          </button>
                        )}

                        <button
                          onClick={() => deleteComment(comment.id)}
                          disabled={actionLoading === comment.id}
                          className="inline-flex items-center text-sm text-red-600 hover:text-red-700"
                        >
                          <TrashIcon className="h-4 w-4 mr-1" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Page {page} of {totalPages}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminComments;
